import React, { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useWS } from "@/contexts/WebSocketContext";
import { useBilling } from "@/contexts/BillingContext";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Brand from "@/components/Brand";
import SubscriptionGate from "@/components/SubscriptionGate";
import TrialCountdown from "@/components/TrialCountdown";
import SoundToggle from "@/components/SoundToggle";
import BrowserPrintAgent from "@/components/BrowserPrintAgent";
import PrinterAutoConnect from "@/components/PrinterAutoConnect";
import PrintProblemAlert from "@/components/PrintProblemAlert";
import PiattoProntoNotifier from "@/components/PiattoProntoNotifier";
import { installGestureUnlock, dingDong, chimeReady, bellCustomerCall, blipTerminato } from "@/lib/audioAlerts";
import {
    LayoutDashboard, LayoutGrid, Calendar, ShoppingBasket, UtensilsCrossed,
    ChefHat, Receipt, FileText, Users, Printer, Plug, BarChart3, Settings,
    LogOut, Menu as MenuIcon, X, Sparkles, CreditCard, CheckCircle2, XCircle, Clock,
    BookOpen, Smartphone, Tablet, Monitor, ChevronDown, HelpCircle, BellRing,
    Wine, Pizza, ScrollText, TrendingUp, Database, Eye,
} from "lucide-react";
import { useUiLayout } from "@/contexts/UiLayoutContext";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "cashier"] },
    { to: "/insights", label: "Insights AI Sala", icon: Sparkles, roles: ["admin", "cashier"], highlight: true },
    { to: "/mappa-sala", label: "Mappa Sala", icon: LayoutGrid, roles: ["admin", "cashier", "waiter"] },
    { to: "/prenotazioni", label: "Prenotazioni", icon: Calendar, roles: ["admin", "cashier", "waiter"] },
    { to: "/nuovo-ordine", label: "Nuovo Ordine", icon: ShoppingBasket, roles: ["admin", "cashier", "waiter"] },
    { to: "/menu", label: "Menu", icon: UtensilsCrossed, roles: ["admin", "cashier", "waiter"] },
    { to: "/menus", label: "Gestione Menu", icon: BookOpen, roles: ["admin", "cashier"] },
    { to: "/menu-del-giorno", label: "Menu del Giorno", icon: ScrollText, roles: ["admin", "cashier"], highlight: true },
    { to: "/menu-schedule", label: "Programmazione Menu", icon: Clock, roles: ["admin"] },
    { to: "/cucina", label: "Terminale Cucina", icon: ChefHat, roles: ["admin", "kitchen"] },
    { to: "/bar", label: "Terminale Bar", icon: Wine, roles: ["admin", "kitchen", "bar"] },
    { to: "/pizzeria", label: "Terminale Pizzeria", icon: Pizza, roles: ["admin", "kitchen", "pizza"] },
    { to: "/cassa", label: "Terminale Cassa", icon: Receipt, roles: ["admin", "cashier"] },
    { to: "/conti-tavoli", label: "Conti Tavoli (vista)", icon: Eye, roles: ["waiter"] },
    { to: "/terminali", label: "Terminali Camerieri", icon: Users, roles: ["admin"] },
    { to: "/scontrini", label: "Scontrini & Fatture", icon: FileText, roles: ["admin", "cashier"] },
    { to: "/chiamate-archivio", label: "Archivio Chiamate", icon: BellRing, roles: ["admin", "cashier", "waiter"] },
    { to: "/utenti", label: "Utenti", icon: Users, roles: ["admin"] },
    { to: "/stampanti", label: "Stampanti", icon: Printer, roles: ["admin"] },
    { to: "/prenova", label: "Collega renova360", icon: Plug, roles: ["admin"] },
    { to: "/billing", label: "Abbonamento", icon: CreditCard, roles: ["admin"] },
    { to: "/report", label: "Report", icon: BarChart3, roles: ["admin", "cashier"] },
    { to: "/statistiche", label: "Statistiche", icon: TrendingUp, roles: ["admin"] },
    { to: "/piatti", label: "Libreria Piatti", icon: Database, roles: ["admin", "cashier"] },
    { to: "/impostazioni", label: "Impostazioni", icon: Settings, roles: ["admin"] },
    { to: "/guida", label: "Guida d'uso", icon: HelpCircle, roles: ["admin", "cashier", "waiter", "kitchen", "bar", "pizza"] },
];

const Layout = () => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { lastEvent } = useWS();
    const billing = useBilling();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const allowed = NAV.filter((n) => n.roles.includes(user.role) || user.role === "admin");

    // Unlock Web Audio on first user gesture (iOS / Chrome autoplay policy)
    useEffect(() => { installGestureUnlock(); }, []);

    useEffect(() => {
        if (!lastEvent) return;
        if (user.role === "kitchen" && lastEvent.type === "new_kitchen_order") {
            dingDong();
            toast("Nuova comanda in arrivo!", { description: "Controlla il terminale cucina." });
        }
        if (user.role === "waiter" && lastEvent.type === "item_ready" && lastEvent.waiter_id === user.id) {
            chimeReady();
            toast("Piatto pronto!", { description: "Vai a ritirare il piatto in cucina." });
        }
        if ((user.role === "admin" || user.role === "cashier") && lastEvent.type === "bill_requested") {
            toast("Conto richiesto", { description: "Un tavolo ha richiesto il conto." });
        }
        if ((user.role === "admin" || user.role === "waiter" || user.role === "cashier") && lastEvent.type === "customer_call_waiter") {
            bellCustomerCall();
            toast(`🔔 Tavolo ${lastEvent.table_number} chiama`, {
                description: lastEvent.note ? `"${lastEvent.note}"` : `Cliente: ${lastEvent.customer_name || "—"}`,
                duration: 8000,
            });
        }
        if ((user.role === "admin" || user.role === "waiter") && lastEvent.type === "kitchen_call_waiter") {
            bellCustomerCall();
            toast(`👨‍🍳 Cucina chiama — Tavolo ${lastEvent.table_number}`, {
                description: lastEvent.note ? `"${lastEvent.note}"` : `Chef: ${lastEvent.kitchen_user || "—"}`,
                duration: 8000,
            });
        }
        if ((user.role === "admin" || user.role === "waiter") && lastEvent.type === "customer_checkin") {
            toast(`🪑 Cliente al tavolo ${lastEvent.table_number}`, { description: lastEvent.customer_name });
        }
        // Piatto segnato come TERMINATO (o RIATTIVATO) dall'admin — avviso operativo per chi sta ordinando
        if (lastEvent.type === "menu_item_updated" && lastEvent.item) {
            const it = lastEvent.item;
            const roles = ["admin", "waiter", "cashier", "kitchen", "bar", "pizza"];
            if (roles.includes(user.role) && typeof it.available === "boolean") {
                if (it.available === false) {
                    blipTerminato();
                    toast(`🍽️ TERMINATO — ${it.name}`, {
                        description: "Il piatto non è più ordinabile",
                        duration: 6000,
                    });
                } else {
                    // RIATTIVATO (toast leggero, no suono per non disturbare)
                    toast(`✓ Riattivato — ${it.name}`, { duration: 3000 });
                }
            }
        }
    }, [lastEvent, user]);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    // Carica nome del ristorante per mostrarlo nella sidebar
    const [restaurantName, setRestaurantName] = useState("");
    useEffect(() => {
        if (!user) return;
        api.get("/restaurant/profile").then(({ data }) => {
            setRestaurantName(data?.name || "");
        }).catch(() => setRestaurantName(""));
    }, [user]);

    const conn = billing?.prenovaConn;
    const aiTotal = billing?.billing?.ai_total_remaining ?? null;

    return (
        <div className="min-h-screen flex bg-deep text-warm">
            <aside
                className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-40 w-72 bg-[#08101c] border-r border-[#162033] flex flex-col h-screen lg:h-screen transform transition-transform ${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
                }`}
                data-testid="sidebar"
            >
                <div className="h-20 px-6 flex items-center border-b border-[#162033] flex-shrink-0">
                    <div className="flex flex-col">
                        <Brand size="md" />
                        {restaurantName && (
                            <div className="text-[10px] uppercase tracking-[0.2em] text-brand mt-1 truncate max-w-[200px]" data-testid="sidebar-restaurant-name">
                                {restaurantName}
                            </div>
                        )}
                    </div>
                </div>

                <nav className="flex-1 py-3 overflow-y-auto overscroll-contain min-h-0">
                    {allowed.map((n) => {
                        const active = location.pathname.startsWith(n.to);
                        const Icon = n.icon;
                        return (
                            <Link
                                key={n.to}
                                to={n.to}
                                onClick={() => setSidebarOpen(false)}
                                data-testid={`nav-${n.to.replace(/\//g, "")}`}
                                className={`flex items-center gap-3 px-5 py-2.5 text-sm font-medium transition-colors btn-touch relative ${
                                    active
                                        ? "bg-[#111c2e] text-brand border-l-2 border-brand"
                                        : "text-elegant hover:text-warm hover:bg-[#0e1626] border-l-2 border-transparent"
                                }`}
                            >
                                <Icon className={`w-4 h-4 flex-shrink-0 ${n.highlight && !active ? "text-brand" : ""}`} />
                                <span className="truncate">{n.label}</span>
                                {n.highlight && !active && (
                                    <span className="ml-auto text-[8px] uppercase tracking-wider bg-brand/15 text-brand px-1.5 py-0.5 rounded flex-shrink-0">AI</span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                <div className="border-t border-[#162033] p-3 flex-shrink-0">
                    {user.role === "admin" && (
                        <div className="mb-2">
                            <TrialCountdown />
                        </div>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-9 h-9 rounded-full bg-[#111c2e] border border-[#1f2c44] flex items-center justify-center text-brand font-bold text-sm flex-shrink-0">
                            {user.name?.[0]?.toUpperCase() || "U"}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{user.name}</div>
                            <div className="text-[10px] uppercase tracking-wider text-brand">
                                {user.role}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        data-testid="logout-button"
                        className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-md border border-[#1f2c44] text-elegant hover:text-warm hover:border-brand hover:bg-[#0e1626] transition-colors text-xs"
                    >
                        <LogOut className="w-3.5 h-3.5" />
                        <span>Esci</span>
                    </button>
                </div>
            </aside>

            {sidebarOpen && (
                <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
            )}

            <div className="flex-1 flex flex-col min-w-0">
                <header className="h-16 lg:h-20 px-4 lg:px-8 flex items-center justify-between border-b border-[#162033] bg-[#08101c]/60 backdrop-blur sticky top-0 z-20">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        <button className="lg:hidden p-2 text-warm" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="sidebar-toggle">
                            {sidebarOpen ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
                        </button>
                        <div className="font-display text-base lg:text-lg tracking-wide text-elegant truncate">
                            {NAV.find((n) => location.pathname.startsWith(n.to))?.label || "Smart Table"}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 lg:gap-3">
                        {/* Sound toggle (audible to roles that have alerts) */}
                        {["admin", "kitchen", "waiter", "cashier"].includes(user.role) && (
                            <SoundToggle />
                        )}

                        {/* renova360 connection indicator */}
                        <button
                            onClick={() => navigate("/prenova")}
                            data-testid="prenova-status-indicator"
                            title={conn ? `${conn.connected ? "Sincronizzato con renova360" : "Disconnesso"}: ${conn.message}` : "Caricamento..."}
                            className={`hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-medium transition-colors ${
                                !conn || !conn.configured ? "border-[#1f2c44] text-elegant" :
                                conn.connected ? "border-brand bg-brand-soft text-brand" :
                                "border-amber-700/50 bg-amber-900/20 text-amber-300"
                            }`}
                        >
                            <Plug className="w-3.5 h-3.5" />
                            <span className={`w-1.5 h-1.5 rounded-full ${
                                !conn || !conn.configured ? "bg-elegant" :
                                conn.connected ? "bg-brand animate-pulse" :
                                "bg-amber-400"
                            }`} />
                            <span className="uppercase tracking-wider hidden md:inline">
                                {!conn ? "..." : !conn.configured ? "renova360" : conn.connected ? "Sync" : "Off"}
                            </span>
                        </button>

                        {/* AI quota indicator */}
                        {(user.role === "admin" || user.role === "cashier") && billing?.billing && (
                            <button
                                onClick={() => navigate("/billing")}
                                data-testid="ai-quota-indicator"
                                title={`${aiTotal} richieste AI rimanenti (${billing.billing.ai_free_remaining} gratuite + ${billing.billing.ai_purchased_remaining} acquistate)`}
                                className={`hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-medium transition-colors ${
                                    aiTotal > 0 ? "border-brand bg-brand-soft text-brand" : "border-amber-700/50 bg-amber-900/20 text-amber-300"
                                }`}
                            >
                                <Sparkles className="w-3.5 h-3.5" />
                                <span className="font-bold">{aiTotal}</span>
                                <span className="uppercase tracking-wider hidden md:inline text-[10px] opacity-80">AI</span>
                            </button>
                        )}

                        {/* Subscription status (admin) */}
                        {user.role === "admin" && billing?.billing && (
                            <button
                                onClick={() => navigate("/billing")}
                                data-testid="subscription-indicator"
                                title={billing.billing.subscription_active ? "Abbonamento attivo" : "Nessun abbonamento attivo"}
                                className={`hidden lg:flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-medium ${
                                    billing.billing.subscription_active ? "border-brand bg-brand-soft text-brand" : "border-red-700/40 bg-red-900/20 text-red-300"
                                }`}
                            >
                                {billing.billing.subscription_active ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                                <span className="uppercase tracking-wider">{billing.billing.subscription_active ? "Premium" : "Free"}</span>
                            </button>
                        )}

                        <div className="hidden xl:block text-xs uppercase tracking-[0.2em] text-brand pl-2 border-l border-[#162033]">
                            {new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long" })}
                        </div>

                        <UserMenu user={user} onLogout={handleLogout} />
                    </div>
                </header>
                <main className="flex-1 p-4 lg:p-8 animate-fade-in">
                    {/* Trial banner */}
                    {billing?.billing?.is_trial && billing.billing.subscription_active && billing.billing.days_remaining <= 10 && (
                        <div className="mb-4 bg-brand-soft border border-brand/40 rounded-md px-4 py-3 flex items-center gap-3 text-sm flex-wrap" data-testid="trial-banner">
                            <Clock className="w-4 h-4 text-brand" />
                            <span className="text-warm">
                                <strong className="text-brand">Trial gratuito</strong> — {billing.billing.days_remaining} giorni rimasti.
                            </span>
                            {user.role === "admin" && (
                                <button onClick={() => navigate("/billing")} className="ml-auto text-xs uppercase tracking-wider text-brand hover:underline">
                                    Attiva Premium →
                                </button>
                            )}
                        </div>
                    )}
                    <SubscriptionGate>
                        <Outlet />
                    </SubscriptionGate>
                </main>
                <Footer />
                <BrowserPrintAgent />
                <PrinterAutoConnect />
                <PrintProblemAlert />
                <PiattoProntoNotifier />
            </div>
        </div>
    );
};

const Footer = () => (
    <footer className="border-t border-[#1f2c44] bg-deep px-4 lg:px-8 py-4 text-[11px] text-elegant flex items-center justify-between flex-wrap gap-2" data-testid="app-footer">
        <div>
            © 2026 <strong className="text-warm">MAKAI di Maria Laura Pellino</strong> · P.IVA 09936410969 · Tutti i diritti riservati.
        </div>
        <div className="flex items-center gap-3">
            <Link to="/privacy" className="hover:text-brand transition-colors" data-testid="footer-privacy">Privacy</Link>
            <span className="opacity-30">·</span>
            <Link to="/cookies" className="hover:text-brand transition-colors" data-testid="footer-cookies">Cookie</Link>
            <span className="opacity-30">·</span>
            <Link to="/termini" className="hover:text-brand transition-colors" data-testid="footer-terms">Termini</Link>
        </div>
    </footer>
);

const CookieBanner = () => null; // moved to App.js GlobalCookieBanner

const LAYOUT_OPTIONS = [
    { id: "smartphone", label: "Smartphone", icon: Smartphone, hint: "Compatto, max 480px" },
    { id: "tablet", label: "Tablet", icon: Tablet, hint: "Bilanciato, max 1024px" },
    { id: "desktop", label: "Desktop", icon: Monitor, hint: "Schermo intero" },
];

const UserMenu = ({ user, onLogout }) => {
    const [open, setOpen] = useState(false);
    const { mode, setMode } = useUiLayout();
    const ref = React.useRef(null);

    React.useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const currentOpt = LAYOUT_OPTIONS.find((o) => o.id === mode) || LAYOUT_OPTIONS[2];
    const CurrentIcon = currentOpt.icon;

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="user-menu-toggle"
                className="flex items-center gap-2 h-9 px-2 lg:px-3 rounded-full border border-[#1f2c44] hover:border-brand transition-colors"
                title={`Layout corrente: ${currentOpt.label}`}
            >
                <div className="w-7 h-7 rounded-full bg-[#111c2e] border border-[#1f2c44] flex items-center justify-center text-brand text-xs font-bold">
                    {user.name?.[0]?.toUpperCase() || "U"}
                </div>
                <CurrentIcon className="w-3.5 h-3.5 text-elegant hidden md:block" />
                <ChevronDown className={`w-3.5 h-3.5 text-elegant transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    data-testid="user-menu-dropdown"
                    className="absolute right-0 top-12 w-72 bg-[#08101c] border border-[#1f2c44] rounded-lg shadow-2xl shadow-black/50 z-50 overflow-hidden"
                >
                    <div className="px-4 py-3 border-b border-[#162033]">
                        <div className="text-sm font-semibold truncate">{user.name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-brand">{user.role}</div>
                    </div>

                    <div className="px-4 py-3 border-b border-[#162033]">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-elegant mb-2">Modalità Layout</div>
                        <div className="grid grid-cols-3 gap-1">
                            {LAYOUT_OPTIONS.map((opt) => {
                                const Icon = opt.icon;
                                const active = mode === opt.id;
                                return (
                                    <button
                                        key={opt.id}
                                        onClick={() => { setMode(opt.id); }}
                                        data-testid={`layout-option-${opt.id}`}
                                        title={opt.hint}
                                        className={`flex flex-col items-center gap-1 py-2 rounded-md border text-[10px] uppercase tracking-wider transition-colors ${
                                            active ? "border-brand bg-brand/10 text-brand" : "border-[#162033] text-elegant hover:border-brand/40"
                                        }`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        <span>{opt.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="text-[10px] text-elegant mt-2 leading-relaxed">{currentOpt.hint}</div>
                    </div>

                    <button
                        onClick={() => { setOpen(false); onLogout(); }}
                        data-testid="user-menu-logout"
                        className="w-full px-4 py-3 text-left text-sm text-elegant hover:bg-[#0e1626] hover:text-warm flex items-center gap-2"
                    >
                        <LogOut className="w-4 h-4" /> Esci
                    </button>
                </div>
            )}
        </div>
    );
};

export default Layout;
